import { AUTH_TOKEN, AUTH_ME, USER_LOGOUT, REFRESH_TOKEN, FCM_SUBSCRIBE } from '../../config/apiEndpoints';
import config from '../../config/index';
import apiRequest from '../../utils/apiRequest';
import { getSavedState, saveState } from '../../utils/saved-state-action';
import { getFCM, isNotificationGranted } from '../../firebase';

export const state = {
  user: getSavedState('resto.currentUser') || {},
  loggingIn: false,
  loggingOut: false,
  gettingToken: false,
  token: getSavedState('resto.currentSession') || {},
  refreshToken: getSavedState('resto.refreshToken') || null,
  fcm_token: getSavedState('resto.fcm_token') || null,
};

export const mutations = {
  AUTH_REQUEST(state) {
    state.loggingIn = true;
  },
  AUTH_REQUEST_SUCCESS(state, payload) {
    state.loggingIn = false;
    state.user = payload.data;

    if (state.user.is_resto_admin) {
      state.user.filtered_permissions = state.user.filtered_permissions || [];
      state.user.filtered_permissions.push('is_resto_admin');
    }

    saveState('resto.currentUser', state.user);
  },
  AUTH_REQUEST_FAIL(state) {
    state.loggingIn = false;
  },
  TOKEN_REQUEST(state) {
    state.gettingToken = true;
    state.refreshToken = null;
    saveState('resto.refreshToken', null);
  },
  TOKEN_REQUEST_SUCCESS(state, payload) {
    state.gettingToken = false;
    state.token = payload.data;
    state.refreshToken = payload.data.refresh_token;
    saveState('resto.currentSession', state.token, (state.token.expires_in * 1000));
    saveState('resto.refreshToken', state.refreshToken);
    setTimeout(() => {
      window.location = '/';
    }, 1000);
  },
  FCM_TOKEN_REQUEST_SUCCESS(state, payload) {
    state.fcm_token = payload;
    saveState('resto.fcm_token', state.fcm_token);
  },
  TOKEN_REQUEST_FAIL(state) {
    state.gettingToken = false;
    state.refreshToken = null;
    saveState('resto.refreshToken', null);
  },
  LOGOUT_REQUEST(state) {
    state.loggingOut = true;
  },
  LOG_OUT(state) {
    state.loggingOut = false;
    state.user = {};
    state.token = null;
    state.refreshToken = null;

    saveState('resto:auth', null);
    saveState('resto.currentUser', null);
    saveState('resto.currentSession', null, 0);
    saveState('isChain', false, 0);
    saveState('resto.fcm_token', null);
    saveState('resto.refreshToken', null);
  },
  SET_TOKEN(state, token) {
    state.token = token;
  },
};

export const getters = {
  loggingIn: state => state.loggingIn,
  loggedIn: state => state.token && state.token.access_token && state.user && state.user.email,
  token: state => state.token || {},
  refreshToken: state => state.refreshToken || null,
  user: state => state.user || {},
  userName: state => state.user.name || '',
  userAvatar: state => state.user.profile_pic || '',
  userEmail: state => state.user.email || '',
  roles: state => state.user.roles || [],
  permissions: state => state.user.filtered_permissions || [],
};

export const actions = {
  async logIn({ commit, dispatch, getters }, { username, password }) {
    const tokenMeta = {
      url: AUTH_TOKEN,
      method: 'POST',
      data: {
        username,
        password,
      },
    };

    commit('TOKEN_REQUEST');

    return apiRequest(tokenMeta)
      .then(async (authResponse) => {
        commit('TOKEN_REQUEST_SUCCESS', authResponse);
        return dispatch('authenticateUser');
      })
      .catch((err) => {
        commit('TOKEN_REQUEST_FAIL');
        throw err;
      });
  },
  async getNewToken({ commit, dispatch, state }) {
    const tokenMeta = {
      url: REFRESH_TOKEN,
      method: 'POST',
      data: {
        refresh_token: state.refreshToken,
      },
    };

    commit('TOKEN_REQUEST');
    return apiRequest(tokenMeta)
      .then((authResponse) => {
        commit('TOKEN_REQUEST_SUCCESS', authResponse);
        return dispatch('authenticateUser');
      })
      .catch((err) => {
        commit('TOKEN_REQUEST_FAIL');
        throw err;
      });
  },
  logOut({ commit, dispatch, state }) {
    const sessionData = state.token;
    const logoutActions = () => {
      commit('LOG_OUT');
    };
    if (sessionData) {
      commit('LOGOUT_REQUEST');
      return apiRequest({
        url: USER_LOGOUT,
        method: 'POST',
        data: {
          registration_token: state.fcm_token,
        },
      }).then(logoutActions)
        .catch(logoutActions);
    }
  },
  authenticateUser({ commit, dispatch, state }) {
    commit('AUTH_REQUEST');

    return new Promise((resolve, reject) => {
      apiRequest({
        url: AUTH_ME,
      })
        .then((userResponse) => {
          commit('AUTH_REQUEST_SUCCESS', userResponse);
          // Subscribe FCM all user type except admin
          if (userResponse && userResponse.data && (userResponse.data.type !== 'admin')) {
            // FCM get token
            const messaging = getFCM();
            // Only get token if notification is granted and messaging is supported
            if (messaging && isNotificationGranted()) {
              messaging.getToken({ vapidKey: config.VAPID_KEY })
                .then((fcmCurrentToken) => {
                  if (fcmCurrentToken) {
                    const fcmData = {
                      url: FCM_SUBSCRIBE,
                      method: 'POST',
                      data: {
                        registration_token: fcmCurrentToken,
                      },
                    };
                    apiRequest(fcmData).catch(err => { throw err; });
                    commit('FCM_TOKEN_REQUEST_SUCCESS', fcmCurrentToken);
                  } else {
                    throw new Error('No registration token available. Request permission to generate one.');
                  }
                }).catch((err) => { throw err; });
            }
          }

          resolve();
        })
        .catch((err) => {
          commit('AUTH_REQUEST_FAIL');
          reject(err);
          throw err;
        });
    });
  },
  validateToken({ commit, dispatch, state }) {
    const currentSession = getSavedState('resto.currentSession');
    commit('SET_TOKEN', currentSession);
  },
  async registerFCMToken({ commit, state }) {
    // don't register fcm token for admin
    if (state.user && state.user.type === 'admin') {
      return;
    }

    // Check if service worker is supported
    if (!('serviceWorker' in navigator)) {
      return;
    }

    const messaging = getFCM();
    if (!messaging) {
      return;
    }

    try {
      // Wait for service worker registration
      const registration = await navigator.serviceWorker.ready;
      if (!registration) {
        throw new Error('No service worker registration found');
      }

      // Ensure service worker is active
      if (registration.active) {
      } else {
        await new Promise(resolve => {
          registration.addEventListener('activate', () => {
            resolve();
          });
        });
      }

      // Wait a bit to ensure the service worker is fully activated
      await new Promise(resolve => setTimeout(resolve, 1000));

      const fcmCurrentToken = await messaging.getToken({ vapidKey: config.VAPID_KEY, serviceWorkerRegistration: registration });

      if (fcmCurrentToken) {
        const fcmData = {
          url: FCM_SUBSCRIBE,
          method: 'POST',
          data: {
            registration_token: fcmCurrentToken,
          },
        };
        try {
          await apiRequest(fcmData);
          commit('FCM_TOKEN_REQUEST_SUCCESS', fcmCurrentToken);
        } catch (error) {
          throw error;
        }
      } else {
        throw new Error('No registration token available. Request permission to generate one.');
      }
    } catch (err) {
      throw err;
    }
  },
};
